import { MapContainer, TileLayer, CircleMarker, Tooltip, Polyline } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { trackSegments } from "@/data/trackSegments";

export type EventCategory = "Messe" | "Motorsport";

export type TrackId = keyof typeof trackSegments | "luedinghausenOval";

export type RaceLocation = {
  name: string;
  lat: number;
  lng: number;
  category: EventCategory;
  date?: string;
  url?: string;
  trackId?: TrackId;
};

const categoryColors: Record<EventCategory, string> = {
  Messe: "#22c55e",
  Motorsport: "#f59e0b",
};

// Simple oval for grass track Lüdinghausen (no detailed OSM data)
const luedinghausenOval: readonly (readonly [number, number])[][] = [
  [
    [51.7705, 7.4390], [51.7712, 7.4405], [51.7710, 7.4422], [51.7700, 7.4430],
    [51.7690, 7.4425], [51.7688, 7.4408], [51.7695, 7.4392], [51.7705, 7.4390],
  ],
];

export const allTrackSegments: Record<TrackId, readonly (readonly [number, number])[][]> = {
  ...trackSegments,
  luedinghausenOval,
};

interface Props {
  locations?: RaceLocation[];
}

export const defaultLocations: RaceLocation[] = [
  { name: "ANGA COM Dortmund", lat: 51.5340, lng: 7.4500, category: "Messe", date: "19.–20.05.2026", url: "https://angacom.de/startseite" },
  { name: "AMSC Grasbahnrennen Lüdinghausen", lat: 51.7700, lng: 7.4410, category: "Motorsport", date: "14.05.2026", url: "https://www.amsc-luedinghausen.de", trackId: "luedinghausenOval" },
  { name: "DMV Goodyear Racing Days Nürburgring", lat: 50.3356, lng: 6.9475, category: "Motorsport", date: "29.–31.05.2026", url: "https://www.dmv-goodyear-racingdays.com", trackId: "nuerburgringGP" },
  { name: "THE Tire Cologne", lat: 50.9473, lng: 6.9831, category: "Messe", date: "09.–10.06.2026", url: "https://www.thetire-cologne.de" },
  { name: "IVT Expo Köln", lat: 50.9473, lng: 6.9831, category: "Messe", date: "10.–11.06.2026", url: "https://ivtexpo.com" },
  { name: "Porsche Carrera Cup Nürburgring", lat: 50.3356, lng: 6.9475, category: "Motorsport", date: "26.–28.06.2026", url: "https://www.nuerburgring.de", trackId: "nuerburgringGP" },
  { name: "Euro Moto Assen", lat: 52.9586, lng: 6.5236, category: "Motorsport", date: "14.–18.08.2026", url: "https://euromoto.racing", trackId: "assen" },
  { name: "Porsche Carrera Cup Hockenheimring", lat: 49.3278, lng: 8.5658, category: "Motorsport", date: "11.–13.09.2026", url: "https://www.hockenheimring.de", trackId: "hockenheim" },
];

const RaceMap = ({ locations = defaultLocations }: Props) => {
  return (
    <div className="rounded-2xl overflow-hidden border border-border/30 h-[500px] w-full">
      <MapContainer
        center={[51.0, 8.0]}
        zoom={6}
        scrollWheelZoom={false}
        className="h-full w-full"
        style={{ background: "#140023" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        {Object.values(allTrackSegments).flatMap((segments, ti) =>
          segments.map((seg, si) => (
            <Polyline
              key={`${ti}-${si}`}
              positions={seg as unknown as [number, number][]}
              pathOptions={{ color: "#f59e0b", weight: 2, opacity: 0.9 }}
            />
          ))
        )}
        {locations.map((loc, i) => (
          <CircleMarker
            key={i}
            center={[loc.lat, loc.lng]}
            radius={9}
            pathOptions={{
              color: categoryColors[loc.category],
              fillColor: categoryColors[loc.category],
              fillOpacity: 0.9,
              weight: 2,
            }}
          >
            <Tooltip>
              <strong>{loc.name}</strong>
              {loc.date && (<><br />{loc.date}</>)}
              <br />
              {loc.category}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
};

export default RaceMap;
